import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import monitorApi from "../../api/monitorApi";
import Header from "../../components/Header/Header";
import CustomSelect from "./CustomSelect/CustomSelect";
import { SelectOptionType } from "./CustomSelect/CustomSelectTypes";
import {
  FormDataStateType,
  SelectOptionsStateType,
} from "./UserReportPageTypes";

const UserReportPage: React.FC = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { type } = useParams<{ type: string }>();

  const [formData, setFormData] = useState<FormDataStateType>({});
  const [selectOptions, setSelectOptions] = useState<SelectOptionsStateType>({
    regions: [],
    operators: [],
    internet_types: [],
    vpns: [],
    services: [],
  });
  const allOptionsRef = useRef<SelectOptionsStateType>({
    regions: [],
    operators: [],
    internet_types: [],
    vpns: [],
    services: [],
  });

  const isFormComplete = useMemo(() => {
    let isComplete = !!(formData.region && formData.operator);

    if (type === "vpn") {
      isComplete = isComplete && !!formData.vpn;
    }

    if (type === "internet") {
      isComplete = isComplete && !!formData.internet_type;
    }

    if (type === "service") {
      isComplete = isComplete && !!formData.service;
    }

    return isComplete;
  }, [formData, type]);

  const getFormData = (name: string): string | number => {
    const field = formData[name];
    if (field && typeof field === "object" && "value" in field) {
      return field.value;
    }
    return field || "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const payload = {
      region: getFormData("region"),
      operator: getFormData("operator"),
      vpn: getFormData("vpn"),
      internet_type: getFormData("internet_type"),
      service: getFormData("service"),
      comment: formData.comment || "",
    };

    monitorApi
      .submitReport(payload)
      .then((response) => {
        navigate(`/thank-you/${response.data.report_id}`);
      })
      .catch((error) => {
        console.info("There was an error submitting the report!", error);
      });
  };

  useEffect(() => {
    monitorApi
      .getReportData()
      .then((response) => {
        if (!response.data) {
          throw new Error("Response data is null or undefined");
        }

        const { regions, operators, vpns, internet_types, services } =
          response.data;

        const allOptions: SelectOptionsStateType = {
          regions:
            regions?.map((region: any) => ({
              value: region.id,
              label: region.name,
            })) || [],
          operators:
            operators?.map((operator: any) => ({
              value: operator.id,
              label: operator.name,
              regions: operator.regions,
            })) || [],
          internet_types:
            internet_types?.map((item: any) => ({
              value: item.id,
              label: item.name,
            })) || [],
          vpns:
            vpns?.map((vpn: any) => ({ value: vpn.id, label: vpn.name })) || [],
          services:
            services?.map((service: any) => ({
              value: service.id,
              label: service.name,
            })) || [],
        };

        setSelectOptions(allOptions);
        allOptionsRef.current = allOptions;
      })
      .catch((error) => {
        console.info("There was an error fetching the data!", error);
      });
  }, []);

  const handleFieldChange = useCallback(
    (data: { name: string; type: string; value: SelectOptionType | null }) => {
      const id = data.value?.value;

      setFormData((prevFormData) => {
        const newFormData = {
          ...prevFormData,
          [data.name]: data.value,
        };

        if (id && data.name === "region") {
          // Reset operator when region changes
          return {
            ...newFormData,
            operator: null,
          };
        }

        return newFormData;
      });

      if (id && data.name === "region") {
        const regionId = id;

        let regionOperators = allOptionsRef.current.operators.filter((item) => {
          return item.regions.filter((_item) => _item.id === regionId).length;
        });

        regionOperators = regionOperators.length
          ? regionOperators
          : allOptionsRef.current.operators;

        setSelectOptions((prevSelectOptions) => ({
          ...prevSelectOptions,
          operators: regionOperators,
        }));
      }
    },
    [],
  );

  if (!selectOptions?.regions?.length) {
    return null;
  }

  return (
    <div className="main-body bg-cream md:pb-10">
      <Header />

      <section className="w-full">
        <div className="w-full flex justify-center">
          <div className="container pl-1-25">
            <h2 className="f-size-2 md:f-size-2-5 font-bold mt-5 mb-2-5 md:mb-3-125">
              {t("user_report.header")}
            </h2>

            <div className="flex justify-center">
              <form onSubmit={handleSubmit}>
                <CustomSelect
                  options={selectOptions.regions}
                  name="region"
                  placeholder={t("user_report.region.placeholder")}
                  label={t("user_report.region.label")}
                  handleFieldChange={handleFieldChange}
                />

                <CustomSelect
                  options={selectOptions.operators}
                  name="operator"
                  placeholder={t("user_report.operator.placeholder")}
                  label={t("user_report.operator.label")}
                  handleFieldChange={handleFieldChange}
                />

                {type === "internet" ? (
                  <CustomSelect
                    options={selectOptions.internet_types}
                    name="internet_type"
                    placeholder={t("user_report.internet_type.placeholder")}
                    label={t("user_report.internet_type.label")}
                    handleFieldChange={handleFieldChange}
                  />
                ) : null}

                {type === "vpn" ? (
                  <CustomSelect
                    options={selectOptions.vpns}
                    name="vpn"
                    placeholder={t("user_report.vpn.placeholder")}
                    label={t("user_report.vpn.label")}
                    handleFieldChange={handleFieldChange}
                  />
                ) : null}

                {type === "service" ? (
                  <CustomSelect
                    options={selectOptions.services}
                    name="service"
                    placeholder={t("user_report.service.placeholder")}
                    label={t("user_report.service.label")}
                    handleFieldChange={handleFieldChange}
                  />
                ) : null}

                <fieldset className="md:mw-28 w-full mb-2-5">
                  <label
                    htmlFor="comment"
                    className="block f-size-1-125 color-green mb-1"
                  >
                    {t("user_report.comment.label")}
                  </label>
                  <textarea
                    id="comment"
                    name="comment"
                    value={formData.comment || ""}
                    onChange={(event) =>
                      setFormData((prevFormData) => ({
                        ...prevFormData,
                        comment: event.target.value,
                      }))
                    }
                    className="w-full p-2 border border-white rounded min-h-24 resize-y bg-white"
                    rows={4}
                  />
                </fieldset>

                <div className="flex justify-center">
                  <button
                    className={`color-white br-0-5 ${
                      isFormComplete ? "bg-dark-green" : "bg-gray"
                    } report-btn mw-16-25 md:mw-21-25 f-size-1-125 md:f-size-1-5 font-bold`}
                    disabled={!isFormComplete}
                  >
                    {t("user_report.button")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UserReportPage;
